/**
 * Categorical colours for the expense donut.
 *
 * Slots 1-5 of the data-viz reference palette, in its fixed order — assigned by rank and
 * never cycled. Both modes were checked with the skill's validator against this app's own
 * surfaces (light #ffffff, dark #191d21) on the adjacent pairlist, which is what a ring is:
 *
 *   light: CVD ΔE 9.1 · normal-vision ΔE 19.6 · contrast WARN on 3 slots
 *   dark:  CVD ΔE 8.4 · normal-vision ΔE 19.3 · contrast PASS
 *
 * Verified 2026-08-19. The light-mode contrast warning obliges relief, which this screen
 * ships twice over: every slice carries a visible label, and the list underneath is the
 * table view. Colour is never the only thing telling two categories apart.
 *
 * Only five categories are ever named — `toDonutSlices` groups the tail — so there is no
 * sixth hue to invent.
 */
const SERIES_LIGHT = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4'] as const;
const SERIES_DARK = ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181'] as const;

/** "Còn lại" is a group, not an identity, so it is deliberately neutral. */
const REST_LIGHT = '#a8b0ba';
const REST_DARK = '#6e7883';

export type ChartTheme = 'light' | 'dark';

export function seriesColor(index: number, theme: ChartTheme): string {
  const scale = theme === 'dark' ? SERIES_DARK : SERIES_LIGHT;
  // Clamped rather than wrapped: two categories must never share a colour.
  return scale[Math.min(index, scale.length - 1)] ?? scale[0];
}

export function restColor(theme: ChartTheme): string {
  return theme === 'dark' ? REST_DARK : REST_LIGHT;
}

/** The chart surface, so slice borders can cut a real 2px gap between neighbours. */
export function surfaceColor(theme: ChartTheme): string {
  return theme === 'dark' ? '#191d21' : '#ffffff';
}

export function labelColor(theme: ChartTheme): string {
  return theme === 'dark' ? '#a4aeb8' : '#5b6470';
}
